/**
 * render-cards.js — 首頁三區卡片渲染器（categories / clinics / articles）
 *
 * 用途：把三組資料陣列以 template literal 渲染成卡片 HTML，mount 進首頁對應容器。
 *   執行即跑（module 載入時 mount，非 export 工廠）——故只在首頁載入（隨 main.js）。
 *
 * 載入鏈（硬性順序，見 src/main.js）：本檔必須在 scroll-reveal.js 與 carousel.js 之前 import。
 *   否則 IntersectionObserver 與 scroll-snap carousel 在各自 mount 時，本檔產出的
 *   .clinic-card / .article-card / .category-card 尚未存在 DOM，進場動畫 / 輪播全抓不到。
 *
 * 主要 export：無（side-effect module，底部三行 mountList 即入口；helper 全為 module 私有）。
 *
 * 陷阱：
 *   1. 所有進 template 的 data 字串必經 escapeHtml（防 XSS），新增欄位勿漏。
 *   2. 三種卡的 reveal 進場策略不同（見各 renderer 上方註解）：category 走 CSS nth-child
 *      每排重置 stagger、clinic 走 data-reveal-defer 交 carousel.js、article 走 data-reveal-stagger。
 *      改動 reveal 屬性前先讀那段 WHY，避免破壞「每排對齊 / 逐張進場」。
 *
 * DEMO: 卡片資料來源 CATEGORIES / CLINICS / ARTICLES 為示意資料（見 ../data/*.js），正式待客戶或後端。
 * TODO(backend): data 檔改為 fetch 後填入相同結構即可，renderer 與 mount 邏輯不需動
 *   （資料形狀為穩定契約，欄位定義見 docs/handoff/HANDOFF_BACKEND.md §5 資料模型）。
 */

import { CATEGORIES } from '../data/categories.js';
import { CLINICS } from '../data/clinics.js';
import { ARTICLES } from '../data/articles.js';
// XSS 防護：所有 data 字串進 template 前先 escape（共用 util，原 inline 同式收斂）
import { escapeHtml } from '../utils/dom.js';

/* ---------- helpers ---------- */

/** 由原圖檔名衍生 .webp 檔名（換副檔名）。對應 scripts/generate-webp.js 產出的同名 sibling。
 *  原圖仍是 <picture> 的 <img> fallback；webp source 找不到時瀏覽器自動退回 jpg/png。 */
const webpName = (file) => String(file).replace(/\.(jpe?g|png)$/i, '.webp');

// 子路徑部署安全：data 的 href 存「頁面檔名」（如 'products.html'），這裡在 runtime 用
// import.meta.env.BASE_URL 拼成 base-aware 連結。Vite 只正規化 HTML 的 <a href>，不碰 JS
// innerHTML 字串，故裸 '/products' root-絕對在子路徑（.../2026-medproof/dist/）會掉前綴打網域根
// → 404（postprocess-dist.js 也只改靜態 HTML 的 <a href>，不改本檔 JS-render 的 href）。
// base:'./' 下 BASE_URL='./' → './products.html'，與 icon.js:34 / product-compare.js 同手法。
// 卡片皆 mount 於 root 首頁（index.html），相對 './<page>.html' 在 dev 與 build 都解析正確。
const baseHref = (file) => `${import.meta.env.BASE_URL}${file}`;

/* ---------- inline SVG icons ---------- */

const ICON_PIN = `<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
  <path d="M7 1c-2.5 0-4.5 2-4.5 4.5C2.5 9 7 13 7 13s4.5-4 4.5-7.5C11.5 3 9.5 1 7 1z" fill="none" stroke="currentColor" stroke-width="1.2" />
  <circle cx="7" cy="5.5" r="1.5" fill="none" stroke="currentColor" stroke-width="1.2" />
</svg>`;

const ICON_STAR = `<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
  <path d="M7 1l1.8 3.7 4.2.6-3 3 .7 4.2L7 10.5l-3.7 2 .7-4.2-3-3 4.2-.6L7 1z" fill="currentColor" />
</svg>`;

/* ---------- clinic logo（顯示端 Pass 2）----------
 * 比照 src/partials/_clinic-logo.html：有 logo → <img>；無 → 首字 placeholder。
 * 卡片在照片上 → 一律加 --on-photo（白底襯墊 + 半透明白邊 + 軟陰影，讓 logo 浮起）。
 * sizeCls 由呼叫端給（clinic-card 直式照 40 / 其他比照）。data.logo 有值 → <img>；缺值才退首字。 */
const renderClinicLogo = (data, sizeCls) => {
  const name = String(data.name || '');
  const initial = name.length ? name.charAt(0).toUpperCase() : '診';
  const alt = data.logoAlt ? String(data.logoAlt) : (name ? `${name} 標誌` : '診所標誌');
  const inner = data.logo
    ? `<img class="clinic-logo__img" src="${escapeHtml(data.logo)}" alt="${escapeHtml(alt)}" width="64" height="64" loading="lazy" />`
    : `<span class="clinic-logo__initial" aria-hidden="true">${escapeHtml(initial)}</span>`;
  return `<span class="clinic-logo ${sizeCls} clinic-logo--on-photo">${inner}</span>`;
};

/* ---------- category card ---------- */

// stagger 不走 data-reveal-stagger（會讓 scroll-reveal.js 把累加序號 inline 寫死在卡上、
// 第 2 排卡 720ms cap）。改由 category-card.css 以 .category-list > li:nth-child() 依 grid 欄數
// 「每排重置」--data-stagger（custom property 由 li 繼承到卡），RWD 欄數變動時 CSS 自動對齊。
/** 渲染單張分類卡（圖 + 中英雙標題 overlay）。 */
const renderCategoryCard = (data) => `
        <li>
          <a class="category-card" href="${escapeHtml(baseHref(data.href))}" data-reveal="fade-up">
            <picture class="category-card__media">
              <source type="image/webp" srcset="./images/categories/${escapeHtml(webpName(data.img))}" />
              <img src="./images/categories/${escapeHtml(data.img)}" alt="${escapeHtml(data.alt)}" width="300" height="200" loading="lazy" />
            </picture>
            <span class="category-card__overlay" aria-hidden="true"></span>
            <span class="category-card__title">
              <span class="category-card__title-en">${escapeHtml(data.titleEn)}</span>
              <span class="category-card__title-zh">${escapeHtml(data.titleZh)}</span>
            </span>
          </a>
        </li>`;

/* ---------- clinic card ---------- */

// clinic 卡用 data-reveal-defer 把進場時機交給 carousel.js（依水平捲動位置逐張 reveal），
// 不在此設 data-reveal-stagger — scroll-reveal.js 會跳過 [data-reveal-defer]，
// carousel.js 才動態給每張 --data-stagger，確保「可見視窗內」與「切下一組」都乾淨逐張進場。
// 註：rating / count（評分與評論數）為示意資料，正式待客戶或後端（資料源見檔頭 DEMO / data/clinics.js）。
/** 渲染單張診所卡（照片 + logo + 名稱 + 地點 + 評分 + 認證標章 + hashtag）。 */
const renderClinicCard = (data) => `
        <li>
          <a class="clinic-card" href="${escapeHtml(baseHref(data.href))}" data-reveal="fade-up" data-reveal-defer>
            <picture class="clinic-card__media">
              <source type="image/webp" srcset="./images/clinics/${escapeHtml(webpName(data.img))}" />
              <img src="./images/clinics/${escapeHtml(data.img)}" alt="${escapeHtml(data.name)}室內空間" width="400" height="600" loading="lazy" />
            </picture>
            <div class="clinic-card__title-row">
              <div class="clinic-card__info">
                <div class="clinic-card__name-row">
                  ${renderClinicLogo(data, 'clinic-logo--40')}
                  <h3 class="clinic-card__name">${escapeHtml(data.name)}</h3>
                </div>
                <p class="clinic-card__location">
                  ${ICON_PIN}
                  <span>${escapeHtml(data.location)}</span>
                </p>
                <p class="clinic-card__rating" aria-label="${escapeHtml(data.rating)} 顆星，共 ${escapeHtml(data.count)} 則評論">
                  ${ICON_STAR}
                  <span class="clinic-card__rating-value">${escapeHtml(data.rating)}</span>
                  <span class="clinic-card__rating-count" aria-hidden="true">(${escapeHtml(data.count)})</span>
                </p>
              </div>
              <div class="clinic-card__badges" aria-label="認證標章">
                <div class="clinic-card__badges-row">
                  <img class="clinic-card__badge" src="./icons/badge-platform.svg" alt="" width="60" height="60" aria-hidden="true">
                  <img class="clinic-card__badge" src="./icons/badge-mohw.svg" alt="" width="60" height="60" aria-hidden="true">
                </div>
                <span class="clinic-card__badges-caption">衛福部合規</span>
              </div>
            </div>
            <ul class="clinic-card__hashtags">
              ${data.tags.map((t) => `<li class="hashtag">${escapeHtml(t)}</li>`).join('\n              ')}
            </ul>
          </a>
        </li>`;

/* ---------- article card ---------- */

/** 渲染單張專欄卡（圖 + 審核標章 + 分類/日期 meta + 標題）。idx 供 data-reveal-stagger 逐張進場。 */
const renderArticleCard = (data, idx) => `
        <li>
          <a class="article-card" href="${escapeHtml(baseHref(data.href))}" data-reveal="fade-up" data-reveal-stagger="${idx}">
            <div class="article-card__media">
              <picture>
                <source type="image/webp" srcset="./images/articles/${escapeHtml(webpName(data.img))}" />
                <img src="./images/articles/${escapeHtml(data.img)}" alt="${escapeHtml(data.alt)}" width="400" height="305" loading="lazy" />
              </picture>
              <div class="article-card__badges">
                <picture>
                  <source media="(max-width: 768px)" srcset="${baseHref('icons/badge-ve-s.svg')}">
                  <img class="article-card__badge" src="${baseHref('icons/badge-ve-m.svg')}" alt="" width="60" height="60" loading="lazy" aria-hidden="true">
                </picture>
                <picture>
                  <source media="(max-width: 768px)" srcset="${baseHref('icons/badge-ci-s.svg')}">
                  <img class="article-card__badge" src="${baseHref('icons/badge-ci-m.svg')}" alt="" width="60" height="60" loading="lazy" aria-hidden="true">
                </picture>
              </div>
            </div>
            <div class="article-card__body">
              <div class="article-card__meta">
                <span class="article-card__tag">${escapeHtml(data.tag)}</span>
                <time class="article-card__date" datetime="${escapeHtml(data.date)}">${escapeHtml(data.dateLabel)}</time>
              </div>
              <h3 class="article-card__title">${escapeHtml(data.title)}</h3>
            </div>
          </a>
        </li>`;

/* ---------- mount ---------- */

/** 把 items 經 renderer 渲染後一次性注入 selector 容器。容器不存在（非首頁）即 no-op。 */
const mountList = (selector, items, renderer) => {
  const el = document.querySelector(selector);
  if (!el) return;
  el.innerHTML = items.map((item, idx) => renderer(item, idx)).join('');
};

mountList('[data-component="category-list"]', CATEGORIES, renderCategoryCard);
mountList('[data-component="clinic-list"]', CLINICS, renderClinicCard);
mountList('[data-component="article-list"]', ARTICLES, renderArticleCard);

/* ---------- 動態 reveal 接通 ---------- */
/* render-cards 在 main.js 中先於 scroll-reveal import，所以本檔執行時
 * window.__reobserveReveal 尚未定義。若上游已掛上鉤就呼叫；若尚未
 * 掛上鉤，scroll-reveal.js 的 initial observe 會自動抓到本檔產出的
 * 動態 [data-reveal] 元素（時序：先 render-cards mount → 後 scroll-reveal
 * 抓 [data-reveal] observe），所以這條 fallback 路徑只在「未來新增非
 * 入口階段才呼叫的 render」時派上用場。
 */
if (typeof window !== 'undefined' && typeof window.__reobserveReveal === 'function') {
  window.__reobserveReveal();
}
